import {
  FragmentType,
  GetRepositoryLabelsDocument,
  GetRepositoryLabelsQuery,
  PullMergeabilityDocument,
} from '@mesa/core/graphql';
import { PullRequestState, PullRequestReviewDecision, StatusState, PullSummaryFragmentDoc } from '@mesa/core/graphql';
import type { PullMergeabilityQuery } from '@mesa/core/graphql';
import { GitHubPullAdapter } from '@mesa/core/pull/adapter.github';
import { queryOptions, useMutation } from '@tanstack/react-query';
import { APICallError } from 'ai';
import { toast } from 'sonner';

import { octokit, queryClient } from '@/lib/clients';
import { collaboratorsQuery } from '@/services/repo';

export type MergeStrategy = 'merge' | 'squash' | 'rebase';

export type PullSummaryWithRepo = {
  number: number;
  title: string;
  state: `${PullRequestState}`;
  merged: boolean;
  isDraft: boolean;
  createdAt: string;
  updatedAt: string;
  additions: number;
  deletions: number;
  reviewDecision?: PullRequestReviewDecision | null;
  author?: {
    login: string;
    name?: string | null;
    avatarUrl?: string;
  } | null;
  repository: {
    owner: {
      login: string;
    };
    name: string;
  };
  labels?: {
    nodes?: Array<{
      name: string;
      color: string;
    } | null> | null;
  } | null;
  comments: {
    totalCount: number;
  };
  reviewThreads: {
    nodes?: Array<{
      comments: {
        totalCount: number;
      };
    } | null> | null;
  };
  statusCheckRollup?: {
    state: StatusState;
  } | null;
  repo: string;
  repoId: string;
};
export type PullSummaryWithRepoList = PullSummaryWithRepo[];
export type Pull = Awaited<ReturnType<typeof octokit.rest.pulls.get>>['data'];
export type GithubFileData = Awaited<ReturnType<typeof octokit.rest.pulls.listFiles>>['data'][number];
export type GithubFileStatus = GithubFileData['status'];
export type GithubReview = Awaited<ReturnType<typeof octokit.rest.pulls.listReviews>>['data'][number];
export type GithubReviewState = 'PENDING' | 'COMMENTED' | 'APPROVED' | 'CHANGES_REQUESTED' | 'DISMISSED';
export type MesaReviewState = GithubReviewState | 'AGENT_REVIEWING';

export type GithubRequestedReviewer = Awaited<
  ReturnType<typeof octokit.rest.pulls.listRequestedReviewers>
>['data']['users'][number];
export type GithubCommit = Awaited<ReturnType<typeof octokit.rest.pulls.listCommits>>['data'][number];

// Use the centralized adapter for transforming GraphQL search fragments
export const fromSearchFragment = (props: { pull: FragmentType<typeof PullSummaryFragmentDoc> }) => {
  const pullRecord = GitHubPullAdapter.toPullRecordFromSearchFragment(props);
  // Transform to the shape expected by the frontend
  return {
    nodeId: pullRecord.nodeId,
    id: pullRecord.id,
    author: pullRecord.author,
    owner: pullRecord.owner,
    repo: pullRecord.repo,
    number: pullRecord.number,
    state: pullRecord.state,
    title: pullRecord.title,
    mergeable: pullRecord.mergeable,
    reviewDecision: pullRecord.reviewDecision,
    checkStatus: pullRecord.checkStatusRollup,
    commentCount: pullRecord.commentCount,
    additions: pullRecord.additions,
    deletions: pullRecord.deletions,
    updatedAt: pullRecord.updatedAt,
    createdAt: pullRecord.createdAt,
  };
};

export type PullSearchResult = {
  nodes: FragmentType<typeof PullSummaryFragmentDoc>[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  issueCount: number;
};

export const PAGE_SIZE = 15;

export type PullsSortOrder = {
  field: 'created' | 'updated';
  direction: 'asc' | 'desc';
};

// export const pullSearchQuery = ({
//   owner,
//   searchTerm,
//   filters,
//   sortBy,
// }: {
//   owner: string;
//   searchTerm: string;
//   filters: Record<string, string[]>;
//   sortBy: PullsSortOrder | null;
// }) => {
//   return {
//     queryKey: [owner, 'pulls', 'search', searchTerm, filters, sortBy] as const,
//     staleTime: 1000 * 60, // Consider data stale after 1 minute
//     retry: false, // Don't retry failed requests to avoid rate limits
//     refetchOnWindowFocus: false,
//     placeholderData: keepPreviousData,
//     queryFn: async ({ pageParam }: { pageParam: string | null }) => {
//       let q = `is:pr ${Object.entries(filters)
//         .map(([key, values]) => `${key}:${values.join(' ')}`)
//         .join(' ')} ${searchTerm}`.trim();

//       if (sortBy) {
//         q += ` sort:${sortBy.field}-${sortBy.direction}`;
//       }

//       if (!filters['repo']) {
//         q += ` owner:${owner}`;
//       }

//       const result = await octogql.graphql<SearchPullsQuery>(SearchPulls_Query.toString(), {
//         q,
//         first: PAGE_SIZE,
//         after: pageParam,
//       });

//       return {
//         nodes: result.search.nodes! as FragmentType<typeof PullSummaryFragment>[],
//         pageInfo: {
//           hasNextPage: result.search.pageInfo.hasNextPage,
//           endCursor: result.search.pageInfo.endCursor ?? null,
//         },
//         issueCount: result.search.issueCount,
//       } satisfies PullSearchResult;
//     },
//     initialPageParam: null,
//     getNextPageParam: (lastPage: PullSearchResult) =>
//       lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : null,
//   };
// };

export const pullQuery = (owner: string, repo: string, pullId: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullId],
    queryFn: async () => {
      const { data } = await octokit.rest.pulls.get({ owner, repo, pull_number: pullId });
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

export const diffQuery = (owner: string, repo: string, pullId: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullId, 'diff'],
    queryFn: async () => {
      const { data } = await octokit.rest.pulls.get({
        owner,
        repo,
        pull_number: pullId,
        mediaType: { format: 'diff' },
      });

      const raw = data as unknown as string;
      // Need to cast to string because the type in octokit when using mediaType: { format: 'diff' } is not correct
      return { raw };
    },
    staleTime: 1000 * 60 * 5,
  });

export const pullCommitsQuery = (owner: string, repo: string, pullId: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullId, 'commits'],
    queryFn: async () => {
      const { data } = await octokit.rest.pulls.listCommits({ owner, repo, pull_number: pullId });
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

export const pullFilesQuery = (owner: string, repo: string, pullId: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullId, 'files'],
    queryFn: async () => {
      const { data } = await octokit.rest.pulls.listFiles({
        owner,
        repo,
        pull_number: pullId,
      });
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

export const reviewsQuery = (owner: string, repo: string, pullNumber: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullNumber, 'reviews'],
    queryFn: async () => {
      const reviews = await octokit.paginate(octokit.rest.pulls.listReviews, {
        owner,
        repo,
        pull_number: pullNumber,
        per_page: 100,
      });
      return reviews;
    },
    staleTime: 1000 * 60 * 5,
  });

export type RequestedReviewers = Awaited<ReturnType<typeof octokit.rest.pulls.listRequestedReviewers>>['data'];

export const requestedReviewersQuery = (owner: string, repo: string, pullId: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullId, 'requestedReviewers'],
    queryFn: async (): Promise<RequestedReviewers> => {
      const { data } = await octokit.rest.pulls.listRequestedReviewers({
        owner,
        repo,
        pull_number: pullId,
      });

      // The API already returns the data in the desired shape
      // { users: RequestedUser[], teams: RequestedTeam[] }
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

export const mergeabilityQuery = (owner: string, repo: string, pullNumber: number, pullState?: string) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullNumber, 'mergeability'],
    queryFn: async () => {
      const res = await octokit.graphql<PullMergeabilityQuery>(PullMergeabilityDocument.toString(), {
        owner,
        repo,
        pull: pullNumber,
      });
      return res.repository?.pullRequest?.mergeable;
    },
    // Only poll when the PR is open and not merged
    enabled: pullState === 'OPEN',
    // Smart polling: only refetch when mergeability is unknown
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling if we have a definitive result or if there's an error
      if (!data || data !== 'UNKNOWN' || query.state.error) {
        return false;
      }

      // Less aggressive backoff since GitHub typically takes 5-30 seconds to determine mergeability
      const dataUpdatedAt = query.state.dataUpdatedAt || 0;
      const timeSinceLastUpdate = Date.now() - dataUpdatedAt;

      // Keep polling frequently for the first minute since most determinations happen within 30s
      if (timeSinceLastUpdate < 60000) return 2000; // First minute: poll every 2s
      if (timeSinceLastUpdate < 180000) return 5000; // Next 2 minutes: poll every 5s
      if (timeSinceLastUpdate < 600000) return 10000; // Next 7 minutes: poll every 10s
      return 20000; // After 10 minutes: poll every 20s (less aggressive than 30s)
    },
    // Continue polling even when tab is not focused (GitHub's mergeability calculation can be slow)
    refetchIntervalInBackground: true,
    // Enhanced retry logic using AI SDK error classification
    retry: (failureCount, error) => {
      // Stop retrying after 3 attempts
      if (failureCount >= 3) return false;

      // Use AI SDK's built-in error classification for smarter retry decisions
      if (APICallError.isInstance(error)) {
        // Don't retry if the AI SDK says it's not retryable
        if (!error.isRetryable) return false;

        // Don't retry on client errors (4xx) except for rate limits (429)
        if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500 && error.statusCode !== 429) {
          return false;
        }
      } else {
        // For non-API errors, don't retry on client errors (4xx)
        if (
          error &&
          'status' in error &&
          typeof error.status === 'number' &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false;
        }
      }

      return true;
    },
    retryDelay: (attemptIndex) => Math.min(2000 + attemptIndex * 1000, 5000), // 2s, 3s, 4s, 5s max
    // Keep data fresh but not too aggressive for this use case
    staleTime: 1000 * 30, // 30 seconds
    // Cache for 5 minutes in case user navigates away and back
    gcTime: 1000 * 60 * 5,
  });

export type ReviewAgent = {
  login: string;
  name: string;
  avatar_url: string;
  review_state: MesaReviewState | undefined;
  requested: boolean;
};

export const reviewAgentsQuery = (owner: string, repo: string, pullId: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pullId, 'reviewAgents'],
    queryFn: async (): Promise<ReviewAgent[]> => {
      return [
        // {
        //   login: 'greptile-apps[bot]',
        //   name: 'Greptile',
        //   avatar_url:
        //     'https://avatars.githubusercontent.com/in/867647?s=60&u=aa3010e3d3c59151f75417e9bdd165e7da8811db&v=4',
        //   review_state: undefined,
        //   requested: false,
        // },
        // {
        //   login: 'coderabbit[bot]',
        //   name: 'CodeRabbit',
        //   avatar_url: 'https://www.google.com/s2/favicons?domain=coderabbit.ai&sz=64',
        //   review_state: undefined,
        //   requested: false,
        // },
        // {
        //   login: 'ellipsis-dev[bot]',
        //   name: 'Ellipsis',
        //   avatar_url: 'https://www.google.com/s2/favicons?domain=ellipsis.dev&sz=64',
        //   review_state: undefined,
        //   requested: false,
        // },
        {
          login: 'mesa-dot-dev[bot]',
          name: 'Mesa',
          avatar_url: 'https://avatars.githubusercontent.com/in/1050077?v=4',
          review_state: undefined,
          requested: false,
        },
        ...(import.meta.env.DEV
          ? [
              {
                login: 'mesa-dev-development[bot]',
                name: 'Mesa Development',
                avatar_url: 'https://avatars.githubusercontent.com/in/1050077?v=4',
                review_state: undefined,
                requested: false,
              },
            ]
          : []),
      ];
    },
    staleTime: Infinity,
  });

export const usePullMutations = (owner: string, repo: string, pull_number: number) => {
  const { mutate: updateMetadata } = useMutation({
    mutationFn: async ({
      title,
      body,
      state,
    }: {
      title: string;
      body?: string;
      state?: 'open' | 'closed' | undefined;
    }) => {
      return octokit.rest.pulls
        .update({
          owner,
          repo,
          pull_number,
          title,
          body,
          state,
        })
        .then((r) => r.data);
    },
    onMutate: ({ title, body, state }: { title: string; body?: string; state?: 'open' | 'closed' | undefined }) => {
      const update = {
        title: title,
        ...(body !== undefined && { body }),
        ...(state !== undefined && { state }),
      };
      const oldData = queryClient.getQueryData(pullQuery(owner, repo, pull_number).queryKey);
      queryClient.setQueryData(pullQuery(owner, repo, pull_number).queryKey, (oldData: any) => ({
        ...oldData,
        ...update,
      }));
      return oldData;
    },
    onError: (error, _, prev) => {
      // Revert optimistic update
      queryClient.setQueryData(pullQuery(owner, repo, pull_number).queryKey, (curr: any) => ({
        ...curr,
        ...prev,
      }));

      // Parse and show GitHub API errors
      if (error instanceof Error) {
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.errors?.[0]?.message) {
            toast.error(`Invalid title: ${errorData.errors[0].message}`);
            return;
          }
        } catch {
          // If error parsing fails or it's not a validation error, show generic message
          toast.error('Failed to update title. Please try again.');
        }
      }
    },
  });

  const { mutate: updateBase } = useMutation({
    mutationFn: async ({ newBase, onSettled }: { newBase: string; onSettled?: () => void }) => {
      return octokit.rest.pulls
        .update({
          owner,
          repo,
          pull_number,
          base: newBase,
        })
        .then((r) => {
          if (onSettled) onSettled();
          return r.data;
        })
        .catch((error) => {
          if (onSettled) onSettled();
          throw error;
        });
    },
    onMutate: ({ newBase }: { newBase: string }) => {
      const oldData = queryClient.getQueryData(pullQuery(owner, repo, pull_number).queryKey);
      queryClient.setQueryData(pullQuery(owner, repo, pull_number).queryKey, (oldData: any) => ({
        ...oldData,
        base: { ...oldData.base, ref: newBase },
      }));
      return oldData;
    },
    onError: (error, _, prev) => {
      console.error(error);
      queryClient.setQueryData(pullQuery(owner, repo, pull_number).queryKey, prev);
      toast.error(error instanceof Error ? error.message : 'An unknown error occurred');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pullQuery(owner, repo, pull_number).queryKey });
      // Also invalidate the diff query since changing the base will affect the diff
      queryClient.invalidateQueries({ queryKey: diffQuery(owner, repo, pull_number).queryKey });
    },
  });

  const { mutate: assignAgentToReview } = useMutation({
    mutationFn: async ({ login, action }: { login: string; action: 'add' | 'remove' }) => {
      if (action === 'add') {
        queryClient.setQueryData(reviewAgentsQuery(owner, repo, pull_number).queryKey, (old: any) => {
          return old.map((agent: any) =>
            agent.login === login ? { ...agent, review_state: 'AGENT_REVIEWING', requested: true } : agent
          );
        });
        setTimeout(() => {
          queryClient.setQueryData(reviewAgentsQuery(owner, repo, pull_number).queryKey, (old: any) => {
            return old.map((agent: any) => (agent.login === login ? { ...agent, review_state: 'COMMENTED' } : agent));
          });
        }, 10000);
      } else if (action === 'remove') {
        queryClient.setQueryData(reviewAgentsQuery(owner, repo, pull_number).queryKey, (old: any) => {
          return old.map((agent: any) => (agent.login === login ? { ...agent, requested: false } : agent));
        });
      }
    },
  });

  const { mutate: modifyReviewers } = useMutation({
    mutationFn: async ({ username, action }: { username: string; action: 'add' | 'remove' }) => {
      if (action === 'add') {
        await octokit.rest.pulls.requestReviewers({
          owner,
          repo,
          pull_number,
          reviewers: [username],
        });
      } else {
        await octokit.rest.pulls.removeRequestedReviewers({
          owner,
          repo,
          pull_number,
          reviewers: [username],
        });
      }
    },
    onMutate: async ({ username, action }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: requestedReviewersQuery(owner, repo, pull_number).queryKey,
      });

      // Snapshot the previous value
      const previousReviewers = queryClient.getQueryData(requestedReviewersQuery(owner, repo, pull_number).queryKey);

      // Find the actor in the actors list
      const collaborators = await queryClient.ensureQueryData(collaboratorsQuery(owner, repo));
      const actor = collaborators.find((a) => a.login === username);

      if (!actor) return { previousReviewers };

      // Optimistically update the reviewers list
      queryClient.setQueryData(requestedReviewersQuery(owner, repo, pull_number).queryKey, (old: any) => {
        if (!old) return old;

        if (action === 'add') {
          // Don't add if already exists
          if (old.users.some((r: any) => r.id === actor.id)) return old;

          return {
            ...old,
            users: [
              ...old.users,
              {
                id: actor.id,
                login: actor.login,
                avatar_url: actor.avatar_url,
              },
            ],
          };
        } else {
          // Remove the reviewer
          return {
            ...old,
            users: old.users.filter((r: any) => r.id !== actor.id),
          };
        }
      });

      return { previousReviewers };
    },
    onError: (error, _, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousReviewers) {
        queryClient.setQueryData(requestedReviewersQuery(owner, repo, pull_number).queryKey, context.previousReviewers);
      }

      // Show error toast
      toast.error(error instanceof Error ? error.message : 'An unknown error occurred');
    },
    onSettled: (error) => {
      // Only invalidate if there was an error
      if (error) {
        queryClient.invalidateQueries({
          queryKey: requestedReviewersQuery(owner, repo, pull_number).queryKey,
        });
      }
    },
  });

  const {
    mutate: mergePull,
    isPending: isMerging,
    status: mergeMutationStatus,
  } = useMutation({
    mutationFn: async ({
      mergeStrategy,
      mergeTitle,
      mergeMessage,
      sha,
    }: {
      mergeStrategy: MergeStrategy;
      mergeTitle: string;
      mergeMessage: string;
      sha: string;
    }) => {
      const { data } = await octokit.rest.pulls.merge({
        owner,
        repo,
        pull_number,
        merge_method: mergeStrategy,
        commit_title: mergeTitle,
        commit_message: mergeMessage,
        sha,
      });
      return data;
    },
    onError: (error: any) => {
      console.error('Merge failed:', error);

      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to merge pull request`, errorMessage);
    },
    onSettled: (mergeResult) => {
      // Invalidate the pull request query to refetch the updated status
      queryClient.invalidateQueries({ queryKey: pullQuery(owner, repo, pull_number).queryKey });

      // Invalidate the pulls list to update the PR status in any lists
      queryClient.invalidateQueries({ queryKey: [owner, 'pulls', 'search'] });

      // Update the local cache until the invalidation refetches
      queryClient.setQueryData(pullQuery(owner, repo, pull_number).queryKey, (oldData: any) => ({
        ...oldData,
        state: mergeResult?.merged ? 'merged' : oldData.state,
        mergeCommitSha: mergeResult?.sha,
      }));
    },
  });

  return {
    updateMetadata,
    updateBase,
    modifyReviewers,
    assignAgentToReview,
    mergePull,
    isMerging,
    mergeMutationStatus,
  };
};

export const getFileContentsQuery = (owner: string, repo: string, path: string, baseRef: string, headRef: string) =>
  queryOptions({
    queryKey: [owner, repo, 'file', path, baseRef, headRef],
    queryFn: async () => {
      // Get the file contents through our backend proxy
      const [baseContent, headContent] = await Promise.all([
        fetch(`/api/github/raw/${owner}/${repo}/${baseRef}/${path}`, {
          credentials: 'include',
        })
          .then(async (res) => (res.ok ? res.text() : ''))
          .catch(() => ''),
        fetch(`/api/github/raw/${owner}/${repo}/${headRef}/${path}`, {
          credentials: 'include',
        })
          .then(async (res) => (res.ok ? res.text() : ''))
          .catch(() => ''),
      ]);

      return {
        baseContent,
        headContent,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

export const labelsQuery = (owner: string, repo: string) =>
  queryOptions({
    queryKey: [owner, repo, 'labels'],
    queryFn: async () => {
      type LabelNode = NonNullable<
        NonNullable<NonNullable<GetRepositoryLabelsQuery['repository']>['labels']>['nodes']
      >[number];
      const allLabels: LabelNode[] = [];
      let hasNextPage = true;
      let after: string | null = null;

      while (hasNextPage) {
        const result: GetRepositoryLabelsQuery = await octokit.graphql<GetRepositoryLabelsQuery>(
          GetRepositoryLabelsDocument.toString(),
          {
            owner,
            repo,
            after,
          }
        );

        const labelsConnection = result.repository?.labels;
        const labels = labelsConnection?.nodes || [];
        const pageInfo = labelsConnection?.pageInfo;

        allLabels.push(...labels);
        hasNextPage = pageInfo?.hasNextPage ?? false;
        after = pageInfo?.endCursor ?? null;
      }

      return allLabels.filter((label): label is NonNullable<typeof label> => label !== null);
    },
    staleTime: 1000 * 60 * 5,
  });
